// Approximate FX rates per 1 EUR. Used only to show tourists a rough
// equivalent in their own currency — not for transactions.
import type { LangCode } from "@/lib/i18n";
import { formatPrice } from "@/lib/i18n";

const RATES_PER_EUR: Record<string, number> = {
  EUR: 1,
  USD: 1.08,
  GBP: 0.85,
  JPY: 170,
  CAD: 1.48,
  AUD: 1.65,
  NZD: 1.78,
  CHF: 0.96,
  CNY: 7.85,
  BRL: 5.95,
  MXN: 19.5,
  ARS: 1100,
  CLP: 1020,
  COP: 4400,
  PEN: 4.05,
  NOK: 11.4,
  SEK: 11.6,
  DKK: 7.45,
  ISK: 150,
  PLN: 4.3,
  CZK: 25,
  HUF: 395,
  RON: 4.97,
  RUB: 98,
  TRY: 35,
  UAH: 44,
  KRW: 1480,
  TWD: 34,
  HKD: 8.4,
  SGD: 1.45,
  THB: 38,
  VND: 27500,
  IDR: 17500,
  MYR: 5.05,
  PHP: 62,
  INR: 92,
  AED: 3.97,
  SAR: 4.05,
  ILS: 4.0,
  EGP: 53,
  ZAR: 20.4,
  MAD: 10.8,
  KES: 142,
};

// Default home-currency per UI language. Anglophone users see USD by
// default; everyone else gets a sensible regional currency.
const LANG_CURRENCY: Record<LangCode, string> = {
  en: "USD",
  pt: "EUR",
  fr: "EUR",
  es: "EUR",
  de: "EUR",
  ja: "JPY",
  it: "EUR",
};

export function currencyForLang(lang: LangCode): string {
  return LANG_CURRENCY[lang] ?? "USD";
}

export function convertAmount(amount: number, from: string, to: string): number | null {
  if (!amount || from === to) return amount;
  const fromR = RATES_PER_EUR[from.toUpperCase()];
  const toR = RATES_PER_EUR[to.toUpperCase()];
  if (!fromR || !toR) return null;
  return (amount / fromR) * toR;
}

// Returns "70 lei (≈ €14)" when local currency differs from the user's.
export function formatPriceDual(
  amount: number,
  localCurrency: string,
  lang: LangCode,
): string {
  const localText = formatPrice(amount, localCurrency, lang);
  if (!amount) return localText;
  const userCur = currencyForLang(lang);
  if (userCur.toUpperCase() === localCurrency.toUpperCase()) return localText;
  const converted = convertAmount(amount, localCurrency, userCur);
  if (converted == null) return localText;
  return `${localText} (≈ ${formatPrice(Math.round(converted), userCur, lang)})`;
}
